import type { ReactNode } from "react";
import { Link, useLocation } from "react-router";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { AppSidebar } from "./app-sidebar";

export interface BreadcrumbStep {
  label: string;
  to?: string;
}

interface AppShellProps {
  /** Path-segment override; defaults to deriving from useLocation. */
  breadcrumbs?: BreadcrumbStep[];
  /** Buttons / chips that sit on the right side of the header. */
  actions?: ReactNode;
  /** When true the main content gets no padding wrapper (use for full-bleed pages). */
  bleed?: boolean;
  children: ReactNode;
}

const SEGMENT_LABEL: Record<string, string> = {
  "": "Home",
  train: "Train",
  special: "Special",
  history: "My History",
  reviews: "Reviews",
  best: "Best Responses",
  pick: "Pick Best",
  "pick-history": "Pick History",
  compare: "Compare",
  sessions: "Session",
  admin: "Admin",
  pending: "Pending",
  upload: "Upload",
  editor: "Editor",
  conversations: "Conversations",
  categories: "Categories",
  new: "New",
};

function deriveBreadcrumbs(pathname: string): BreadcrumbStep[] {
  if (pathname === "/") return [{ label: "Home" }];
  const parts = pathname.split("/").filter(Boolean);
  return parts.map((part, i) => {
    const to = "/" + parts.slice(0, i + 1).join("/");
    const isLast = i === parts.length - 1;
    const label = SEGMENT_LABEL[part] ?? decodeURIComponent(part);
    return { label, to: isLast ? undefined : to };
  });
}

export function AppShell({ breadcrumbs, actions, bleed = false, children }: AppShellProps) {
  const location = useLocation();
  const crumbs = breadcrumbs ?? deriveBreadcrumbs(location.pathname);

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="sticky top-0 z-10 flex h-14 shrink-0 items-center gap-2 border-b bg-background px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb className="flex-1">
            <BreadcrumbList>
              {crumbs.map((c, i) => {
                const isLast = i === crumbs.length - 1;
                return (
                  <span key={`${c.to ?? c.label}-${i}`} className="flex items-center gap-1.5">
                    <BreadcrumbItem>
                      {c.to && !isLast ? (
                        <BreadcrumbLink asChild>
                          <Link to={c.to}>{c.label}</Link>
                        </BreadcrumbLink>
                      ) : (
                        <BreadcrumbPage>{c.label}</BreadcrumbPage>
                      )}
                    </BreadcrumbItem>
                    {!isLast && <BreadcrumbSeparator />}
                  </span>
                );
              })}
            </BreadcrumbList>
          </Breadcrumb>
          {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
        </header>
        <main className={bleed ? "flex-1" : "flex-1 p-4 md:p-6"}>{children}</main>
      </SidebarInset>
    </SidebarProvider>
  );
}
